import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";

const line = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: 0,
    transition: { duration: 1.05, delay: 0.25 + i * 0.13, ease: [0.16, 1, 0.3, 1] },
  }),
};

const HERO_IMG =
  "https://images.unsplash.com/photo-1544502062-f82887f03d1c?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen overflow-hidden" data-testid="hero">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 z-0">
        <img src={HERO_IMG} alt="" className="h-full w-full object-cover object-center" />
        <div className="absolute inset-0 bg-background/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />
      </motion.div>

      {/* corner meta */}
      <motion.div style={{ opacity: fade }} className="absolute top-28 left-5 sm:left-8 z-10">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-primary">
          FuzePoint Consulting
        </p>
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-foreground/50 mt-1">
          EOD-Grade Leadership Training
        </p>
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8 min-h-screen flex flex-col justify-end pb-24"
      >
        <h1 className="font-display font-black uppercase tracking-tighter text-foreground leading-[0.85] text-[16vw] sm:text-[13vw] lg:text-[11rem]">
          <span className="hero-line-mask">
            <motion.span custom={0} variants={line} initial="hidden" animate="visible" className="block">
              Defuse
            </motion.span>
          </span>
          <span className="hero-line-mask">
            <motion.span custom={1} variants={line} initial="hidden" animate="visible" className="block">
              Challenges<span className="text-primary">.</span>
            </motion.span>
          </span>
          <span className="hero-line-mask">
            <motion.span
              custom={2}
              variants={line}
              initial="hidden"
              animate="visible"
              className="block text-primary"
            >
              Ignite Potential<span className="text-foreground">.</span>
            </motion.span>
          </span>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-8"
        >
          <p className="max-w-md text-base sm:text-lg text-foreground/75 leading-relaxed">
            Drawing on the precision and composure of Explosive Ordnance Disposal, we train
            emerging leaders to stay calm, think clearly, and chart decisive paths through complexity.
          </p>
          <a
            href="#contact"
            data-testid="hero-cta"
            className="group inline-flex items-center gap-3 self-start bg-primary px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white hover:bg-white hover:text-background transition-colors duration-300"
          >
            Schedule a Consultation
            <ArrowDown className="h-4 w-4 group-hover:translate-y-1 transition-transform duration-300" />
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
}
